import { Dictionary } from '@reduxjs/toolkit';
import { keyBy, reverse, sortBy } from 'lodash';
import {
  InferredProgressValue,
  PastProjectStatus,
  ProgressStatus,
  Project,
  ProjectPhase,
} from 'api/project';
import { StatusGroup } from 'api/tasks';
import { FilterableField } from 'components/organisms/tasks/fields';
import { byCreatedAt } from 'utils/comparators/by-created-at';
import { byStartDate } from 'utils/comparators/by-start-date';

type FieldLookupById = Dictionary<FilterableField>;

export const getFieldMapReducer =
  (fieldLookup: FieldLookupById) =>
  (
    hash: Record<string, unknown>,
    { fieldValue, fieldId }: { fieldId: number; fieldValue: unknown },
  ) => {
    const field = fieldLookup[fieldId];

    if (field) {
      return {
        ...hash,
        [field.fieldName]: fieldValue,
      };
    }

    return hash;
  };

const byCurrentPhaseFilter = (phase: Pick<ProjectPhase, 'fields'>) => {
  const phaseStatus = phase.fields.find((field) => field.fieldId === 1);

  if (phaseStatus) {
    return phaseStatus.fieldValue === 2;
  }

  return false;
};

export const calculateProgressPercent = (
  progressStatus?: ProgressStatus[],
  totalTaskCount = 0,
) => {
  const progressLookup = keyBy(progressStatus ?? [], 'group');
  const completed = progressLookup[StatusGroup.Completed]?.count ?? 0;
  const total = totalTaskCount ?? 0;
  let progress = completed / total;

  progress = Number.isNaN(progress) ? 0 : progress;

  return Math.round(progress * 100);
};

export const lastStatusUpdatedAt = (statuses: PastProjectStatus[]) =>
  reverse(sortBy(statuses, 'updatedAt'))[0]?.updatedAt;

export type ProjectListItem = Omit<
  Project,
  | 'fields'
  | 'projectOwner'
  | 'currentPhases'
  | 'customer'
  | 'partners'
  | 'teamMembers'
  | 'inferredProgress'
> &
  Record<string, unknown> & {
    progressPercent: number;
    meta: Pick<Project, 'customer'>;
  };

type TransformFunction = (
  fieldLookup: FieldLookupById,
) => (data: Project[]) => ProjectListItem[];

export const transform: TransformFunction = (fieldLookup) => (data) => {
  const initial: ProjectListItem[] = [];
  const toFieldMap = getFieldMapReducer(fieldLookup);

  return data
    .sort(byCreatedAt)
    .reverse()
    .reduce(
      (
        list,
        { fields, customer, teamMembers, inferredProgress, partners, ...rest },
      ) => {
        const fieldsMap = (fields ?? []).reduce(toFieldMap, {});
        const { projectPhases = [] } = rest;
        const currentPhases = projectPhases
          .filter(byCurrentPhaseFilter)
          .sort(byStartDate)
          .reverse()
          .map(({ projectPhaseName }) => projectPhaseName);

        const progressPercent = calculateProgressPercent(
          rest.progressStatus,
          rest.totalTaskCount,
        );

        const meta = {
          customer,
        };

        return [
          ...list,
          {
            ...rest,
            ...fieldsMap,
            currentPhases: currentPhases.length > 0 ? currentPhases : null,
            progressPercent,
            meta,
            arr: rest.annualizedRecurringRevenue,
            customer: customer.companyId,
            partnerCompany: partners?.map(({ companyId }) => companyId),
            projectOwner: rest.projectOwner?.userId,
            inferredProgress:
              inferredProgress?.value === InferredProgressValue.None
                ? undefined
                : inferredProgress?.value,
            estimatedDueDate: rest.inferredProgressDays,
            teamMembers: (teamMembers ?? []).map(({ userId }) => userId),
            projectHours: {
              budget_hours: rest.budgetedHours,
              total_minutes: rest.totalMinutes,
              project_name: rest.projectName,
            },
            lastStatusUpdate: lastStatusUpdatedAt(rest.pastProjectStatus ?? []),
            totalAllocatedHours: rest?.metrics?.totalAllocatedHours ?? 0,
          },
        ];
      },
      initial,
    );
};
